import { Button, Option, Persona, makeStyles } from '@fluentui/react-components';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { AccessType, AccessTypeString, TAccessNodeType } from '../../types';
import { useUpdateAccess } from '../../api';
import { ControlledField } from '@shared/components';
import { useCallback, useState } from 'react';
import { AddFilled } from '@fluentui/react-icons';
import { useUsers, TUser } from '@modules/users';
import { accessLabel } from '@shared/backendFrontend';

const schema = yup.object({
  userId: yup.array().of(yup.string().required()).length(1, 'Please select a user.'),
  accessType: yup
    .array(
      yup
        .mixed<AccessTypeString>()
        .oneOf([AccessTypeString.READ, AccessTypeString.EDIT, AccessTypeString.MANAGE])
        .required()
    )
    .length(1, 'Please select access rights.'),
});
type FormData = yup.InferType<typeof schema>;

type TProps = {
  id: string;
  nodeType: TAccessNodeType;
  users: TUser[];
  setIsLoading: (isLoading: boolean) => void;
};

const useStyles = makeStyles({
  input: {
    minWidth: 0,
    width: '220px',
  },
});

const AddUserForm = ({ id, nodeType, users, setIsLoading }: TProps) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { userId: [], accessType: [] },
  });
  const [query, setQuery] = useState('');

  const updateAccess = useUpdateAccess();
  const onSubmit = useCallback(
    (data: FormData) => {
      if (!data || !data.userId || !data.accessType) return;
      setIsLoading(true);
      updateAccess.mutate(
        {
          id,
          nodeType,
          userId: data.userId[0],
          accessType: parseInt(data.accessType[0]),
        },
        {
          onSuccess: () => {
            reset();
            setQuery('');
          },
          onSettled: () => setIsLoading(false),
        }
      );
    },
    [reset, updateAccess]
  );

  const { userMap } = useUsers();
  const children = users
    .filter((user) => user.name?.toLowerCase().includes(query.toLowerCase()))
    .map((user) => (
      <Option key={user._id} value={user._id} text={user.name || 'Unknown'}>
        <Persona
          avatar={{ color: 'colorful', idForColor: user._id, 'aria-hidden': true, size: 28 }}
          name={user.name || 'Unknown'}
          textAlignment="center"
        />
      </Option>
    ));
  if (children.length === 0)
    children.push(
      <p key="no" className="p-2">
        No match.
      </p>
    );

  const classes = useStyles();
  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)} className="mb-1 flex space-x-3">
        <ControlledField
          inputType="combobox"
          getLabel={(userId) => (userMap ? userMap[userId]?.name || 'Unknown' : 'Unknown')}
          label="User"
          placeholder="Select"
          name="userId"
          control={control}
          errors={errors}
          fixedHeight
          value={query}
          setQuery={setQuery}
          className={classes.input}
        >
          {children}
        </ControlledField>
        <ControlledField
          inputType="dropdown"
          getLabel={accessLabel}
          label="Access"
          placeholder="Select"
          name="accessType"
          control={control}
          errors={errors}
          fixedHeight
          className={classes.input}
        >
          <Option value={AccessType.READ.toString()}>Read</Option>
          <Option value={AccessType.EDIT.toString()}>Edit</Option>
          <Option value={AccessType.MANAGE.toString()}>Manage</Option>
        </ControlledField>
        <div className="mt-6 pt-0.5">
          <Button type="submit" appearance="primary" icon={<AddFilled />}>
            Add user
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddUserForm;
